import { Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { evo } from "foldkit/struct";

import { settle, start } from "../../api/asyncData";
import {
	createApiKey,
	deleteApiKey,
	listApiKeys,
} from "../../api/commands/integrations";
import type { Page, PageReturn } from "../shared";
import { Message } from "./message";
import { CopyTarget, type Model } from "./model";

// COMMANDS

export const ListKeys = listApiKeys.command(Message.CompletedListKeys);
export const CreateKey = createApiKey.command(
	Message.CompletedCreateKey,
	({ generation, result }) =>
		Message.CompletedCreateKey({ generation, result }),
);
export const DeleteKey = deleteApiKey.command(
	Message.CompletedDeleteKey,
	({ id, generation, result }) =>
		Message.CompletedDeleteKey({ id, generation, result }),
);

/** v1: write to the clipboard, show "Copied!" for two seconds. The Model flips on click; this clears it. */
export const CopyText = Command.define("CopyText", {
	args: { text: S.String, target: CopyTarget },
	messages: [Message.CopyFeedbackExpired],
	execute: ({ text, target }) =>
		Effect.promise(() => navigator.clipboard.writeText(text)).pipe(
			Effect.catch(() => Effect.void),
			Effect.flatMap(() => Effect.sleep("2 seconds")),
			Effect.as(Message.CopyFeedbackExpired({ target })),
		),
});

// UPDATE

type Return = PageReturn<Model, Message>;

export const initial: Model = {
	page: "Integrations",
	copied: Option.none(),
	keys: AsyncData.Idle(),
	keyDescription: "",
	creating: false,
	revealedKey: Option.none(),
};

export const page: Page<Model, Message> = {
	init: (_route, shared) => ({
		model: evo(initial, { keys: start }),
		commands: [ListKeys({ generation: shared.generation })],
	}),
	changedRoute: (model) => ({ model }),
	update: (model, message, shared) => {
		const current = (generation: number) => generation === shared.generation;
		return Message.match<Return>(message, {
			ClickedCopy: ({ target }) =>
				Option.match(model.revealedKey, {
					onNone: () => ({ model }),
					onSome: (value) => ({
						model: evo(model, { copied: () => Option.some(target) }),
						commands: [CopyText({ text: value, target })],
					}),
				}),
			CopyFeedbackExpired: ({ target }) => ({
				model: Option.contains(model.copied, target)
					? evo(model, { copied: () => Option.none() })
					: model,
			}),
			CompletedListKeys: ({ generation, result }) => ({
				model: current(generation)
					? evo(model, {
							keys: (data) =>
								settle(
									data,
									Result.isSuccess(result)
										? result
										: Result.succeed({ data: [] }),
								),
						})
					: model,
			}),
			ChangedKeyDescription: ({ value }) => ({
				model: evo(model, { keyDescription: () => value }),
			}),
			ClickedCreateKey: () =>
				model.creating
					? { model }
					: {
							model: evo(model, { creating: () => true }),
							commands: [
								CreateKey({
									description: model.keyDescription,
									generation: shared.generation,
								}),
							],
						},
			CompletedCreateKey: ({ generation, result }) =>
				!current(generation)
					? { model }
					: Result.match(result, {
							onSuccess: ({ key }) => ({
								model: evo(model, {
									creating: () => false,
									revealedKey: () => Option.some(key),
									keyDescription: () => "",
									keys: start,
								}),
								commands: [ListKeys({ generation: shared.generation })],
							}),
							onFailure: () => ({
								model: evo(model, { creating: () => false }),
							}),
						}),
			ClickedDeleteKey: ({ id }) => ({
				model,
				commands: [DeleteKey({ id, generation: shared.generation })],
			}),
			CompletedDeleteKey: ({ id, generation, result }) => ({
				model:
					current(generation) && Result.isSuccess(result)
						? evo(model, {
								keys: (data) =>
									AsyncData.isSuccess(data)
										? AsyncData.succeed({
												data: data.data.data.filter((key) => key.id !== id),
											})
										: data,
								revealedKey: () => Option.none(),
							})
						: model,
			}),
		});
	},
};
